import React, { useMemo, useState, useEffect } from 'react';

const BOOKMAKER_COLORS = [
    '#34d399',
    '#f59e0b',
    '#60a5fa',
    '#f472b6',
    '#a78bfa',
    '#fb7185',
    '#4ade80',
    '#22d3ee',
];

function formatPrice(v) {
    if (v == null) return '-';
    return Number(v).toFixed(2);
}

function formatAxisTime(date, isMobile) {
    const d = new Date(date);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    if (isMobile) return `${hh}:${mm}`;
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${month}/${day} ${hh}:${mm}`;
}

function PriceHistoryChart({ priceHistory = [], bookmakerNames = {} }) {
    const [isMobile, setIsMobile] = useState(false);
    const [hoverSeries, setHoverSeries] = useState(null);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const { series, bounds, viewBox, padding } = useMemo(() => {
        const w = isMobile ? 420 : 720;
        const h = isMobile ? 240 : 260;
        const p = isMobile
            ? { top: 16, right: 12, bottom: 28, left: 36 }
            : { top: 20, right: 20, bottom: 32, left: 44 };

        const valid = (priceHistory || []).filter(
            (s) => Array.isArray(s.price_changes) && s.price_changes.length > 0
        );

        if (valid.length === 0) {
            return {
                series: [],
                bounds: { tMin: 0, tMax: 1, pMin: 0, pMax: 1 },
                viewBox: { w, h },
                padding: p,
            };
        }

        let tMin = Infinity, tMax = -Infinity, pMin = Infinity, pMax = -Infinity;
        valid.forEach((s) => {
            s.price_changes.forEach((pt) => {
                const t = new Date(pt.timestamp).getTime();
                if (t < tMin) tMin = t;
                if (t > tMax) tMax = t;
                if (pt.price < pMin) pMin = pt.price;
                if (pt.price > pMax) pMax = pt.price;
            });
        });

        if (tMin === tMax) tMax = tMin + 1;
        const priceRange = pMax - pMin || 1;
        pMin -= priceRange * 0.12;
        pMax += priceRange * 0.12;

        const built = valid.map((s, i) => ({
            bookmaker: s.bookmaker,
            color: BOOKMAKER_COLORS[i % BOOKMAKER_COLORS.length],
            points: s.price_changes.map((pt) => ({
                t: new Date(pt.timestamp).getTime(),
                price: pt.price,
                timestamp: pt.timestamp,
            })),
            open: s.open_price,
            current: s.current_price,
            pct: s.percentage_change,
        }));

        return {
            series: built,
            bounds: { tMin, tMax, pMin, pMax },
            viewBox: { w, h },
            padding: p,
        };
    }, [priceHistory, isMobile]);

    if (series.length === 0) {
        return (
            <div className="py-8 text-center text-xs font-mono text-slate-600">
                No price history available.
            </div>
        );
    }

    const { w, h } = viewBox;
    const innerW = w - padding.left - padding.right;
    const innerH = h - padding.top - padding.bottom;

    const xFor = (t) => padding.left + ((t - bounds.tMin) / (bounds.tMax - bounds.tMin)) * innerW;
    const yFor = (price) =>
        padding.top + innerH - ((price - bounds.pMin) / (bounds.pMax - bounds.pMin)) * innerH;

    const yTicks = 4;
    const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
        bounds.pMin + ((bounds.pMax - bounds.pMin) * i) / yTicks
    );

    const xTicks = isMobile ? 3 : 5;
    const xTickValues = Array.from({ length: xTicks + 1 }, (_, i) =>
        bounds.tMin + ((bounds.tMax - bounds.tMin) * i) / xTicks
    );

    const pathFor = (points) => {
        if (points.length === 0) return '';
        return points
            .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${xFor(pt.t).toFixed(2)} ${yFor(pt.price).toFixed(2)}`)
            .join(' ');
    };

    return (
        <div className="w-full">
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
                {series.map((s) => {
                    const dimmed = hoverSeries && hoverSeries !== s.bookmaker;
                    return (
                        <button
                            key={s.bookmaker}
                            onMouseEnter={() => setHoverSeries(s.bookmaker)}
                            onMouseLeave={() => setHoverSeries(null)}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded border border-slate-800 transition-opacity ${
                                dimmed ? 'opacity-40' : 'opacity-100'
                            }`}
                        >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                                {(bookmakerNames[s.bookmaker] || s.bookmaker).toString()}
                            </span>
                            <span className="text-[10px] font-mono text-slate-600">
                                {formatPrice(s.current)}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="relative">
                <svg
                    viewBox={`0 0 ${w} ${h}`}
                    className="w-full h-auto"
                    preserveAspectRatio="xMidYMid meet"
                >
                    {/* Grid lines */}
                    {yTickValues.map((v, i) => (
                        <g key={`y-${i}`}>
                            <line
                                x1={padding.left}
                                x2={w - padding.right}
                                y1={yFor(v)}
                                y2={yFor(v)}
                                stroke="#1e293b"
                                strokeWidth="1"
                                strokeDasharray="2 3"
                            />
                            <text
                                x={padding.left - 6}
                                y={yFor(v) + 3}
                                textAnchor="end"
                                className="fill-slate-600"
                                style={{ fontSize: isMobile ? 9 : 10, fontFamily: 'ui-monospace, monospace' }}
                            >
                                {formatPrice(v)}
                            </text>
                        </g>
                    ))}

                    {/* X axis ticks */}
                    {xTickValues.map((t, i) => (
                        <text
                            key={`x-${i}`}
                            x={xFor(t)}
                            y={h - padding.bottom + 14}
                            textAnchor="middle"
                            className="fill-slate-600"
                            style={{ fontSize: isMobile ? 9 : 10, fontFamily: 'ui-monospace, monospace' }}
                        >
                            {formatAxisTime(t, isMobile)}
                        </text>
                    ))}

                    {/* Series */}
                    {series.map((s) => {
                        const isHovered = hoverSeries === s.bookmaker;
                        const isDimmed = hoverSeries && !isHovered;
                        return (
                            <g
                                key={s.bookmaker}
                                style={{
                                    opacity: isDimmed ? 0.25 : 1,
                                    transition: 'opacity 0.15s',
                                }}
                            >
                                <path
                                    d={pathFor(s.points)}
                                    fill="none"
                                    stroke={s.color}
                                    strokeWidth={isHovered ? 2 : 1.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {s.points.map((pt, i) => (
                                    <circle
                                        key={i}
                                        cx={xFor(pt.t)}
                                        cy={yFor(pt.price)}
                                        r={i === s.points.length - 1 ? 3 : 2}
                                        fill="#0a0f1e"
                                        stroke={s.color}
                                        strokeWidth="1.5"
                                    />
                                ))}
                                {/* Current label */}
                                {s.points.length > 0 && (
                                    <text
                                        x={xFor(s.points[s.points.length - 1].t) + 5}
                                        y={yFor(s.points[s.points.length - 1].price) - 5}
                                        className="fill-current"
                                        style={{
                                            fontSize: isMobile ? 9 : 10,
                                            fontFamily: 'ui-monospace, monospace',
                                            fontWeight: 600,
                                            fill: s.color,
                                        }}
                                    >
                                        {formatPrice(s.points[s.points.length - 1].price)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
}

export default PriceHistoryChart;
